import {
  DEFAULT_REPORT_TABS as ANALYTICS_DEFAULT_REPORT_TABS,
  ANALYTICS_REPORTS_TABS_ID,
} from '@src/providers/useAnalyticsTabs';
import { LedgerAccountTypeEnum, notEmpty } from '@talos/kyoko';
import { compact } from 'lodash-es';
import type { RouteMatch } from 'react-router-dom';
import type { useTabLabelResolver } from '../../hooks/useTabLabelResolver';
import type { NavigationItem } from '../../tokens/navigationPaths';
import { DEFAULT_MONITORING_BLOTTER_TABS } from '../Monitoring/Blotters';
import { MONITORING_BLOTTER_PREFIX } from '../Monitoring/Blotters/types';
import { resolvePortfolioBreadcrumb } from '../Portfolio/breadcrumbUtils';
import {
  getIdFromAnalyticsRoute,
  getIdFromMonitoringRoute,
  isCurrentRouteAdmin,
  isCurrentRouteAnalytics,
  isCurrentRouteDealer,
  isCurrentRouteDeepDive,
  isCurrentRouteMarket,
  isCurrentRouteMonitoring,
  isCurrentRouteMonitoringBlotter,
  isCurrentRouteOrderDetails,
  isCurrentRoutePortfolioEngineering,
  isCurrentRouteSettings,
  isCurrentRouteTalosAdmin,
  isCurrentRouteTrading,
} from './routes';

export const mapPathToDisplayParts = (
  resolveTabLabel: ReturnType<typeof useTabLabelResolver>['resolveTabLabel'],
  path: string,
  routeMatches: RouteMatch<string, NavigationItem>[]
): (string | undefined)[] => {
  if (isCurrentRouteMonitoringBlotter(path)) {
    const tabId = getIdFromMonitoringRoute(path);
    const label = resolveTabLabel({
      tabSetID: MONITORING_BLOTTER_PREFIX,
      tabID: tabId,
      nonPersistedTabs: DEFAULT_MONITORING_BLOTTER_TABS,
    });
    return ['monitoring', 'blotters', label || tabId];
  }

  // Simple case where the route matches a single navigation item
  if (
    isCurrentRouteSettings(path) ||
    isCurrentRoutePortfolioEngineering(path) ||
    isCurrentRouteDealer(path) ||
    isCurrentRouteAdmin(path) ||
    isCurrentRouteTrading(path) ||
    isCurrentRouteTalosAdmin(path) ||
    isCurrentRouteMonitoring(path)
  ) {
    return routeMatches.map(routeMatch => routeMatch.route.label).filter(notEmpty);
  }

  if (isCurrentRouteAnalytics(path)) {
    const tabID = getIdFromAnalyticsRoute(path);
    const label = resolveTabLabel({
      tabSetID: ANALYTICS_REPORTS_TABS_ID,
      tabID,
      nonPersistedTabs: ANALYTICS_DEFAULT_REPORT_TABS,
    });
    return compact(['analytics', label ?? tabID]);
  }

  if (path.startsWith('/portfolio/')) {
    const portfolioBreadcrumb = resolvePortfolioBreadcrumb(path);
    if (portfolioBreadcrumb) {
      return portfolioBreadcrumb;
    }
  }

  return path.split('/').slice(1); // ignore initial trailing white space
};

export const mapDisplayPartToPath = (parts: string[], currentPath: string): string | undefined => {
  // handle cases where clicking on ['settings', 'user groups'] get's translated to 'settings/user-groups' instead
  const escaped = parts.map(p => p?.replace(/ /g, '-'));

  const isTrading =
    isCurrentRouteMarket(currentPath) || isCurrentRouteOrderDetails(currentPath) || isCurrentRouteDeepDive(currentPath);
  if (isTrading) {
    return escaped.length === 1 ? 'trading' : undefined;
  }
  return escaped.join('/');
};

/** Resolves the route params from an account ledger events route. Does some parsing to make sure that we ingest these params uniformly. */
export function getAccountLedgerEventsRouteParams(searchParams: URLSearchParams) {
  const asset = searchParams.get('asset') || undefined; // normalize ("" | null) -> undefined to simplify things and align with internal representation
  // the others don't need to be defaulted to undefined because they are not optional where undefined (null) becomes a valid state
  const account = searchParams.get('account') || undefined;
  const accountType = searchParams.get('accountType') || undefined;
  return {
    asset,
    account,
    accountType: isLedgerAccountTypeEnum(accountType) ? accountType : undefined,
  };
}

function isLedgerAccountTypeEnum(value: string | undefined | null): value is LedgerAccountTypeEnum {
  return value === LedgerAccountTypeEnum.MarketAccount || value === LedgerAccountTypeEnum.SubAccount;
}
